import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenIcon } from '@heroicons/react/24/outline';

// Help guides live on the landing site at /resources/{language}/{slug}. They exist
// in Portuguese and Spanish so far; nothing is shown in other languages.
const LANDING_URL = process.env.REACT_APP_LANDING_URL || 'https://aiqda.com';

const LABELS = {
    pt: { help: 'Ajuda', all: 'Todos os guias' },
    es: { help: 'Ayuda', all: 'Todas las guías' },
};

// One guide per app page and language; project tabs use `project-tab-{id}` (ids from DetailProject).
export const GUIDES = {
    dashboard: {
        pt: ['aiqda-primeiros-passos', 'Primeiros passos no AIQDA'],
        es: ['aiqda-primeros-pasos', 'Primeros pasos en AIQDA'],
    },
    projects: {
        pt: ['aiqda-criar-projeto', 'Como criar um projeto'],
        es: ['aiqda-crear-proyecto', 'Cómo crear un proyecto'],
    },
    'project-tab-10': {
        pt: ['aiqda-criar-questionario', 'Criar um questionário'],
        es: ['aiqda-crear-cuestionario', 'Crear un cuestionario'],
    },
    'project-tab-0': {
        pt: ['aiqda-preparacao-de-dados', 'Preparação de dados'],
        es: ['aiqda-preparacion-de-datos', 'Preparación de datos'],
    },
    'project-tab-2': {
        pt: ['aiqda-livro-de-codigos', 'Organizar o livro de códigos'],
        es: ['aiqda-libro-de-codigos', 'Organizar el libro de códigos'],
    },
    'project-tab-4': {
        pt: ['aiqda-descoberta-de-categorias-ia', 'Descoberta de categorias com IA'],
        es: ['aiqda-descubrimiento-de-categorias-ia', 'Descubrimiento de categorías con IA'],
    },
    'project-tab-5': {
        pt: ['aiqda-pesquisa-de-categorias-ia', 'Pesquisa de categorias com IA'],
        es: ['aiqda-busqueda-de-categorias-ia', 'Búsqueda de categorías con IA'],
    },
    'project-tab-1': {
        pt: ['aiqda-analise-perguntas-ia', 'Fazer perguntas às entrevistas'],
        es: ['aiqda-analisis-preguntas-ia', 'Hacer preguntas a las entrevistas'],
    },
    'project-tab-7': {
        pt: ['aiqda-resultados-graficos-tabelas', 'Gráficos e tabelas automáticos'],
        es: ['aiqda-resultados-graficos-tablas', 'Gráficos y tablas automáticos'],
    },
    'project-tab-9': {
        pt: ['aiqda-base-de-bibliografia', 'Base de Bibliografia'],
        es: ['aiqda-base-bibliografica', 'Base bibliográfica'],
    },
    'project-tab-8': {
        pt: ['aiqda-criar-artigo-com-ia', 'Escrever um artigo com IA'],
        es: ['aiqda-crear-articulo-con-ia', 'Escribir un artículo con IA'],
    },
    'text-coder': {
        pt: ['aiqda-codificacao-manual', 'Codificação manual'],
        es: ['aiqda-codificacion-manual', 'Codificación manual'],
    },
    analysis: {
        pt: ['aiqda-rever-editar-transcricao', 'Rever e editar a transcrição'],
        es: ['aiqda-revisar-editar-transcripcion', 'Revisar y editar la transcripción'],
    },
    article: {
        pt: ['aiqda-verificar-referencias-exportar-artigo', 'Verificar referências e exportar'],
        es: ['aiqda-verificar-referencias-exportar-articulo', 'Verificar referencias y exportar'],
    },
};

export default function GuideLinks({ page, className = '' }) {
    const { i18n } = useTranslation();
    const language = i18n.language?.slice(0, 2);
    const guide = GUIDES[page]?.[language];

    if (!guide) return null;

    const [slug, title] = guide;
    const labels = LABELS[language];
    return (
        <nav aria-label={labels.help} data-testid="guide-links" className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm ${className}`}>
            <span className="flex items-center gap-1.5 font-medium text-gray-700">
                <BookOpenIcon className="w-4 h-4" aria-hidden="true" />
                {labels.help}:
            </span>
            <a href={`${LANDING_URL}/resources/${language}/${slug}`} target="_blank" rel="noreferrer"
               className="text-amber-800 underline hover:text-amber-900">
                {title}
            </a>
            <span aria-hidden="true" className="text-gray-400">·</span>
            <a href={`${LANDING_URL}/resources/${language}`} target="_blank" rel="noreferrer"
               className="text-amber-800 underline hover:text-amber-900">
                {labels.all}
            </a>
        </nav>
    );
}
